// Pure sessionStorage helpers for the impersonated client, kept separate from
// ImpersonationContext.tsx so api-client.ts and the context module can both
// depend on this without forming a circular import (the context now also
// calls into api-client.ts to auto-select a client).
//
// sessionStorage (not localStorage) is deliberate: it's scoped per tab, so an
// admin impersonating Client A in one tab and Client B in another doesn't
// have the two tabs fight over a single global identity. The tradeoff is
// that a brand-new tab starts with no impersonation selected — the
// auto-select-first-client fallback in ImpersonationContext handles that.

export interface StoredImpersonatedClient {
  id: string;
  name: string;
}

export const IMPERSONATED_CLIENT_STORAGE_KEY = "impersonatedClient";

// Guard: reject stale integer IDs from before the UUID migration.
function isValidStoredClient(value: unknown): value is StoredImpersonatedClient {
  return (
    !!value &&
    typeof (value as StoredImpersonatedClient).id === "string" &&
    (value as StoredImpersonatedClient).id.startsWith("clie_")
  );
}

export function readStoredImpersonatedClient(): StoredImpersonatedClient | null {
  if (typeof window === "undefined") return null;
  const saved = sessionStorage.getItem(IMPERSONATED_CLIENT_STORAGE_KEY);
  if (!saved) return null;
  try {
    const parsed = JSON.parse(saved);
    if (isValidStoredClient(parsed)) return parsed;
    sessionStorage.removeItem(IMPERSONATED_CLIENT_STORAGE_KEY);
    return null;
  } catch {
    sessionStorage.removeItem(IMPERSONATED_CLIENT_STORAGE_KEY);
    return null;
  }
}

export function getImpersonatedClientId(): string | null {
  return readStoredImpersonatedClient()?.id ?? null;
}

/**
 * Suffixes a sessionStorage/localStorage key with the currently impersonated
 * client's id, so per-page cached UI state (filters, pagination, sort, etc.)
 * doesn't leak across an admin's impersonated clients within the same browser
 * tab — an admin switching from Client A to Client B would otherwise read back
 * Client A's cached page/filter selections under a global key, restoring a
 * page number or filter that doesn't correspond to Client B's actual data.
 * Returns the bare key unchanged for a regular (non-impersonating) user, who
 * only ever has one client, so there's no leak risk to scope against.
 */
export function scopedStorageKey(baseKey: string): string {
  const clientId = getImpersonatedClientId();
  return clientId ? `${baseKey}:${clientId}` : baseKey;
}
